import React from "react";
import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import { PasswordSection } from "features/auth/PasswordSection";
import { displayResultMessage } from "utils/misc";
import { useHistory } from "react-router-dom";
import { SECURITY_SETTINGS_PATH } from "common/constants";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import { ConfigurationCard } from "features/settings/common/styles";
import { ChangePasswordRequest, LanguageNameSpaces } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { DarkModePassword, Password } from "common/Icons/Icons.tsx";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title.tsx";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { triggerClearBiometric } from "services/webview/webViewBridgeUtils.ts";
import { useSelector } from "react-redux";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookFormPanel, {
  NewLookFormActions,
} from "features/settings/NewLook/NewLookFormPanel.tsx";

export const ChangePasswordPage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const [changePassword, changePasswordStatus] =
    sharedLib.sharedApi.authentication.useChangePasswordMutation();
  const history = useHistory();
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const PasswordIcon = theme.isDarkMode ? DarkModePassword : Password;
  const maxWidth = isBottomNavigator ? "100%" : "375px";
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const onFinish = async ({
    oldPass,
    password,
  }: {
    oldPass: string;
    password: string;
  }) => {
    const changePassRequest: ChangePasswordRequest = {
      new_password: password,
      old_password: oldPass,
    };
    displayResultMessage(
      changePassword(changePassRequest),
      t("change_password.success_message"),
      t("change_password.error_message")
    )
      .catch(() => {})
      .then(() => {
        triggerClearBiometric(user?.email).catch(() => {});
        history.replace(SECURITY_SETTINGS_PATH);
      });
  };

  const oldPasswordItem = (
    <Form.Item
      name="oldPass"
      label={t("change_password.old_password_label")}
      rules={[
        {
          required: true,
          message: t("change_password.old_password_required"),
        },
      ]}
    >
      <Input.Password
        placeholder={t("change_password.old_password_placeholder")}
      />
    </Form.Item>
  );

  if (theme.isNewLook) {
    return (
      <NewLookFormPanel
        title={t("change_password.title")}
        onBack={() => history.goBack()}
        backLabel={tCommon("page_title.account_and_security")}
      >
        <Form onFinish={onFinish} layout="vertical">
          {oldPasswordItem}
          <PasswordSection showConfirm={true} />
          <NewLookFormActions>
            <Button
              type="primary"
              htmlType="submit"
              loading={changePasswordStatus.isLoading}
            >
              {t("change_password.update_button")}
            </Button>
          </NewLookFormActions>
        </Form>
      </NewLookFormPanel>
    );
  }

  return (
    <Flex
      $justifyContent="center"
      $alignItems="center"
      $flexDirection="column"
      style={{
        margin: `${isBottomNavigator ? 16 : 32}px 12px`,
      }}
    >
      <BackWrapper style={{ maxWidth, width: "100%" }}>
        <Flex $justifyContent={"flex-start"}>
          <ArrowLeft onClick={() => history.goBack()} />
          <Title level={3} style={{ marginLeft: 12 }}>
            {t("change_password.title")}
          </Title>
        </Flex>
      </BackWrapper>
      <ConfigurationCard style={{ width: maxWidth }}>
        <PasswordIcon
          style={{
            display: "block",
            width: 125,
            height: "auto",
            margin: "0 auto 16px auto",
          }}
        />
        <Form onFinish={onFinish} layout="vertical">
          {oldPasswordItem}
          <PasswordSection showConfirm={true} />
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: "100%" }}
            loading={changePasswordStatus.isLoading}
          >
            {t("change_password.update_button")}
          </Button>
        </Form>
      </ConfigurationCard>
    </Flex>
  );
};

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;
const ArrowLeft = styled(ArrowLeftOutlined)`
  font-size: 18px;
  cursor: pointer;
`;
